import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, Headset, CheckCircle2, UserCog, RefreshCw } from "lucide-react";
import { api, usePortalAuth, extractErr } from "@/portal/PortalAuth";
import { btn, ghost, card, inp } from "@/portal/PortalShell";

const BUCKETS = ["New", "Waiting", "Active", "Resolved"];

export default function LiveChatInbox() {
  const { user } = usePortalAuth();
  const isAdmin = user.role === "admin";
  const [bucket, setBucket] = useState("New");
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [convo, setConvo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadList = useCallback(async () => {
    try { const { data } = await api.get("/admin/chat/conversations"); setList(data || []); } catch { /* */ }
  }, []);
  const loadConvo = useCallback(async (id) => {
    if (!id) return;
    try { const { data } = await api.get(`/admin/chat/conversations/${id}`); setConvo(data); } catch (e) { toast.error(extractErr(e)); }
  }, []);

  useEffect(() => { api.get("/admin/chat/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) api.get("/users").then(({ data }) => setStaff((data || []).filter((u) => ["admin", "manager", "reservations"].includes(u.role)))).catch(() => {}); }, [isAdmin]);

  // Deep-link from the email alert: ?chat=<id>
  useEffect(() => {
    const cid = new URLSearchParams(window.location.search).get("chat");
    if (cid) setActiveId(cid);
  }, []);

  useEffect(() => { loadList(); const t = setInterval(loadList, 4000); return () => clearInterval(t); }, [loadList]);
  useEffect(() => { if (!activeId) return; loadConvo(activeId); const t = setInterval(() => loadConvo(activeId), 3000); return () => clearInterval(t); }, [activeId, loadConvo]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [convo?.messages?.length]);

  const filtered = list.filter((c) => c.bucket === bucket);
  const counts = BUCKETS.reduce((a, b) => ({ ...a, [b]: list.filter((c) => c.bucket === b).length }), {});
  const unreadTotal = list.reduce((n, c) => n + (c.unread_for_staff || 0), 0);

  const saveSetting = async (patch) => {
    try { const { data } = await api.put("/admin/chat/settings", patch); setSettings(data); toast.success("Chat settings updated."); }
    catch (e) { toast.error(extractErr(e)); }
  };
  const reply = async () => {
    const t = text.trim(); if (!t || sending) return;
    setSending(true);
    try { await api.post(`/admin/chat/conversations/${activeId}/reply`, { text: t }); setText(""); await loadConvo(activeId); loadList(); }
    catch (e) { toast.error(extractErr(e)); } finally { setSending(false); }
  };
  const act = async (fn, okMsg) => { try { await fn(); toast.success(okMsg); await loadConvo(activeId); loadList(); } catch (e) { toast.error(extractErr(e)); } };
  const resolve = () => act(() => api.post(`/admin/chat/conversations/${activeId}/resolve`), "Conversation resolved.");
  const resumeAI = () => act(() => api.post(`/admin/chat/conversations/${activeId}/ai`, { enabled: true }), "AI assistance resumed.");
  const assign = (email, name) => act(() => api.post(`/admin/chat/conversations/${activeId}/assign`, { assignee_email: email, assignee_name: name }), "Conversation assigned.");

  return (
    <div data-testid="live-chat-inbox" className="space-y-4">
      {/* global controls */}
      {settings && (
        <div className={card + " flex flex-wrap items-center justify-between gap-3"} data-testid="chat-global-settings">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">AI Guest Concierge</p>
            <p className="text-sm text-[#44403C] mt-0.5">Master controls for the public website chat. {unreadTotal > 0 && <span className="text-[#8A5A2B] font-medium">{unreadTotal} unread</span>}</p>
          </div>
          <div className="flex items-center gap-5">
            <SettingToggle label="AI auto-replies" checked={!!settings.ai_enabled} onChange={(v) => saveSetting({ ai_enabled: v })} testid="toggle-ai-master" />
            <SettingToggle label="Management online" checked={!!settings.management_online} onChange={(v) => saveSetting({ management_online: v })} testid="toggle-mgmt-online" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* list */}
        <div className={card + " p-0 overflow-hidden"}>
          <div className="flex border-b border-[#E7E5E4]">
            {BUCKETS.map((b) => (
              <button key={b} onClick={() => setBucket(b)} data-testid={`chat-bucket-${b.toLowerCase()}`}
                className={`flex-1 py-2.5 text-xs font-medium transition-colors ${bucket === b ? "bg-[#1C1917] text-white" : "text-[#44403C] hover:bg-[#F5F5F4]"}`}>
                {b}{counts[b] ? ` (${counts[b]})` : ""}
              </button>
            ))}
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#F5F5F4]" data-testid="chat-conversation-list">
            {filtered.length === 0 && <p className="p-4 text-sm text-[#78716C]">No {bucket.toLowerCase()} conversations.</p>}
            {filtered.map((c) => (
              <button key={c.id} onClick={() => setActiveId(c.id)} data-testid={`chat-convo-${c.id}`}
                className={`w-full text-left p-3.5 transition-colors ${activeId === c.id ? "bg-[#F5F5F4]" : "hover:bg-[#FAFAF9]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-[#1C1917] truncate">{c.guest_name || "Website visitor"}</p>
                  {c.unread_for_staff > 0 && <span data-testid={`chat-unread-${c.id}`} className="shrink-0 text-[10px] bg-[#8A5A2B] text-white rounded-full px-1.5 py-0.5">{c.unread_for_staff}</span>}
                </div>
                <p className="text-xs text-[#78716C] truncate mt-0.5">{c.preview || "—"}</p>
                <p className="text-[10px] text-[#A8A29E] mt-1">{c.assigned_to_name ? `Assigned: ${c.assigned_to_name}` : "Unassigned"} · {c.updated_at?.slice(5, 16).replace("T", " ")}</p>
              </button>
            ))}
          </div>
        </div>

        {/* conversation */}
        <div className={card + " p-0 flex flex-col min-h-[420px]"}>
          {!convo ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#78716C]" data-testid="chat-empty-detail">Select a conversation to read and reply.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#E7E5E4] flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-[#1C1917]">{convo.guest_name || "Website visitor"} {convo.guest_email && <span className="text-[#78716C] font-light">· {convo.guest_email}</span>}</p>
                  <p className="text-[11px] text-[#8A9A86] inline-flex items-center gap-1 mt-0.5">
                    {convo.ai_enabled && !convo.human_requested ? <><Sparkles className="w-3 h-3" /> AI handling</> : <><Headset className="w-3 h-3" /> Human support {convo.resolved ? "· resolved" : ""}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <select data-testid="chat-assign-select" onChange={(e) => { const u = staff.find((x) => x.email === e.target.value); if (u) assign(u.email, u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email); }}
                      value={convo.assigned_to_email || ""} className={inp + " h-9 max-w-[170px] text-xs"}>
                      <option value="">Assign to…</option>
                      {staff.map((u) => <option key={u.id} value={u.email}>{u.name || u.email} ({u.role})</option>)}
                    </select>
                  )}
                  {!convo.resolved && <button onClick={resolve} data-testid="chat-resolve" className={ghost + " text-xs"}><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</button>}
                  {!convo.ai_enabled && <button onClick={resumeAI} data-testid="chat-resume-ai" className={ghost + " text-xs"}><Sparkles className="w-3.5 h-3.5" /> Resume AI</button>}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAF9] max-h-[48vh]" data-testid="chat-detail-messages">
                {(convo.messages || []).map((m) => <AdminBubble key={m.id} m={m} />)}
                <div ref={endRef} />
              </div>

              <div className="border-t border-[#E7E5E4] p-3 flex items-end gap-2">
                <textarea data-testid="chat-reply-input" rows={1} value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); reply(); } }}
                  placeholder="Reply to the guest…" className="flex-1 resize-none max-h-28 rounded-xl border border-[#E7E5E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#8A9A86]" />
                <button onClick={reply} disabled={sending || !text.trim()} data-testid="chat-reply-send" className="w-10 h-10 rounded-full bg-[#1C1917] text-white flex items-center justify-center hover:bg-[#44403C] disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminBubble({ m }) {
  if (m.role === "system") return <p className="text-center text-[11px] text-[#78716C] px-6">{m.text}</p>;
  const guest = m.role === "guest";
  return (
    <div className={`flex ${guest ? "justify-start" : "justify-end"}`} data-testid={`admin-msg-${m.role}`}>
      <div className="max-w-[80%]">
        <p className={`text-[10px] mb-0.5 ${guest ? "text-[#78716C]" : m.role === "staff" ? "text-[#8A9A86]" : "text-[#A8A29E]"}`}>{m.author}</p>
        <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${guest ? "bg-white border border-[#E7E5E4] text-[#1C1917]" : m.role === "staff" ? "bg-[#1C1917] text-white" : "bg-[#E8EFE7] text-[#1C1917]"}`}>{m.text}</div>
      </div>
    </div>
  );
}

function SettingToggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-[#44403C] cursor-pointer">
      <span>{label}</span>
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
        className={`w-11 h-6 rounded-full relative transition-colors ${checked ? "bg-[#1C1917]" : "bg-[#E7E5E4]"}`}>
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-0.5"}`} />
      </button>
    </label>
  );
}
